import React, { useState, useEffect } from 'react';
import { getUnifiedClient } from '../lib/unified-model-client';

export function ModelSelector({ selectedModel, onModelChange }) {
  const [models, setModels] = useState({ ollama: [], cloudflare: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    try {
      const client = getUnifiedClient();
      const available = await client.fetchAvailableModels();
      setModels(available);
    } catch (error) {
      console.error('Failed to load models:', error);
    } finally {
      setLoading(false);
    }
  };

  const allModels = [...models.ollama, ...models.cloudflare];

  const filteredModels = allModels.filter(model => {
    // Filter by provider
    if (filter !== 'all' && model.provider !== filter) return false;

    // Filter by search
    if (search && !model.name.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }

    return true;
  });

  const getModelBadge = (model) => {
    if (model.capabilities?.includes('vision')) return '👁️ Vision';
    if (model.capabilities?.includes('coding')) return '💻 Code';
    if (model.capabilities?.includes('reasoning')) return '🧠 Reasoning';
    return '';
  };

  const getProviderBadge = (provider) => {
    if (provider === 'ollama') return '🦙 Ollama';
    return '☁️ Cloudflare';
  };

  return (
    <div className="model-selector">
      <div className="model-selector-header">
        <h3>Select Model</h3>
        <div className="model-stats">
          <span>{models.ollama.length} Ollama</span>
          <span>{models.cloudflare.length} Cloudflare</span>
        </div>
      </div>

      <div className="model-filters">
        <input
          type="text"
          placeholder="Search models..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="model-search"
        />

        <div className="filter-buttons">
          <button
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            All ({allModels.length})
          </button>
          <button
            className={filter === 'ollama' ? 'active' : ''}
            onClick={() => setFilter('ollama')}
          >
            🦙 Ollama ({models.ollama.length})
          </button>
          <button
            className={filter === 'cloudflare' ? 'active' : ''}
            onClick={() => setFilter('cloudflare')}
          >
            ☁️ Cloudflare ({models.cloudflare.length})
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-models">Loading models...</div>
      ) : (
        <div className="models-list">
          {filteredModels.map(model => (
            <div
              key={`${model.provider}-${model.id}`}
              className={`model-item ${selectedModel === model.id ? 'selected' : ''}`}
              onClick={() => onModelChange(model.id)}
            >
              <div className="model-info">
                <div className="model-name">{model.name}</div>
                <div className="model-badges">
                  <span className="provider-badge">
                    {getProviderBadge(model.provider)}
                  </span>
                  {getModelBadge(model) && (
                    <span className="capability-badge">
                      {getModelBadge(model)}
                    </span>
                  )}
                  {model.size && (
                    <span className="size-badge">
                      {(model.size / 1000000000).toFixed(1)}GB
                    </span>
                  )}
                </div>
              </div>
              {selectedModel === model.id && (
                <div className="selected-indicator">✓</div>
              )}
            </div>
          ))}

          {filteredModels.length === 0 && (
            <div className="no-models">
              No models found matching "{search}"
            </div>
          )}
        </div>
      )}

      <div className="quick-select">
        <h4>Quick Select:</h4>
        <div className="quick-buttons">
          <button onClick={() => onModelChange('deepseek-v4-pro:cloud')}>
            ⚡ DeepSeek V4 Pro
          </button>
          <button onClick={() => onModelChange('qwen3.5:397b-cloud')}>
            🧠 Qwen 3.5 397B
          </button>
          <button onClick={() => onModelChange('mistral-large-3:675b-cloud')}>
            💪 Mistral Large 3
          </button>
          <button onClick={() => onModelChange('qwen3-vl:235b-cloud')}>
            👁️ Qwen 3 Vision
          </button>
        </div>
      </div>
    </div>
  );
}
